/** Dashboard: a full-width decision pipeline followed by the load horizon. */
import React from 'react';
import { useStore } from '../../store';

import { DecisionPipelineBoard } from './DecisionPipelineBoard';
import { LoadHorizon } from './LoadHorizon';

import { PortfolioCard } from './PortfolioCard';
import './dashboard.css';

export interface DashboardPageProps {
  /**
   * Left-rail site filter from the host engine (search + company + verdict +
   * data-scope). The two-act deck narrows to exactly these site ids so the
   * orbit/horizon always agree with the site list beside them. null ⇒ all.
   */
  visibleSiteIds?: ReadonlySet<string> | null;
}

export function DashboardPage({ visibleSiteIds = null }: DashboardPageProps = {}) {
  const companyId = useStore((s) => s.companyId);
  const siteRag = useStore((s) => s.siteRag);
  // Sites and reports hydrate in parallel. Rendering the decision deck after
  // sites alone are ready lets the Orbit backstop fingerprint an empty report
  // list and persist `No source reports yet` for otherwise-scored sites.
  const isLoaded = siteRag.isSitesLoaded && siteRag.isReportsLoaded;
  const hasSites = siteRag.sites.length > 0;

  return (
    <div className="space-y-4 pb-24 dash-reveal px-4 pt-0" data-testid="dashboard-page-v2">
      {!isLoaded ? <section className="instrument-panel" aria-busy="true" aria-label="Loading portfolio" data-testid="portfolio-loading"><p className="type-meta text-text-muted">Reading portfolio composites…</p><div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" aria-hidden>{[1,2,3,4].map(item => <div key={item} className="h-24 rounded border border-border bg-background animate-pulse" />)}</div></section> : !hasSites ? (
        <WelcomeEmptyCard />
      ) : (
        <TwoActStage customerId={companyId} visibleSiteIds={visibleSiteIds} />
      )}
    </div>
  );
}

/** Shared site focus links the pipeline and load horizon. */
function TwoActStage({
  customerId,
  visibleSiteIds = null,
}: {
  customerId: string | null;
  visibleSiteIds?: ReadonlySet<string> | null;
}) {
  const [activeSiteId, setActiveSiteId] = React.useState<string | null>(null);
  return (
    <div className="two-act flex flex-col gap-6" data-testid="dashboard-two-act">

      <section className="w-full min-w-0" aria-label="Portfolio decision pipeline">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border-subtle px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="da-type-eyebrow">Portfolio</span>
          </div>
          <div className="shrink-0">
            <PortfolioCard compact />
          </div>
        </div>
        <DecisionPipelineBoard
          customerId={customerId}
          activeSiteId={activeSiteId}
          onActiveSiteChange={setActiveSiteId}
          visibleSiteIds={visibleSiteIds}
        />
      </section>

      {/* Bottom Row: Horizon (Full Width) */}
      <div className="w-full">
        <LoadHorizon
          customerId={customerId}
          activeSiteId={activeSiteId}
          onActiveSiteChange={setActiveSiteId}
          visibleSiteIds={visibleSiteIds}
        />
      </div>
      
    </div>
  );
}

const WELCOME_STEPS: Array<{ step: string; title: string; body: string }> = [
  { step: '01', title: 'Create a site', body: 'Pin the location, boundary and project intent.' },
  { step: '02', title: 'Attach evidence', body: 'Upload documents and let SED collect the data.' },
  { step: '03', title: 'Generate reports', body: 'Run the diligence set and reach a GO / NO-GO.' },
];

/**
 * First-run welcome — a brand-new user (0 sites) gets the path into the
 * product, not a blank grid: the three-step route to a decision, a New Site
 * CTA (the existing `datacrate:new-site` wizard event) and the guide.
 */
function WelcomeEmptyCard() {
  return (
    <section data-testid="dashboard-welcome-empty" className="relative overflow-hidden rounded-xl border border-border-subtle bg-background/50 backdrop-blur-md px-8 py-12 lg:px-12 lg:py-16 mt-4 shadow-sm">
      {/* Background gradients for a premium feel */}
      <div
        aria-hidden
        className="absolute -right-32 -top-32 w-96 h-96 rounded-full pointer-events-none opacity-50"
        style={{
          background: 'color-mix(in srgb, var(--primary) 20%, transparent)',
          filter: 'blur(80px)',
        }}
      />
      <div
        aria-hidden
        className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full pointer-events-none opacity-30"
        style={{
          background: 'color-mix(in srgb, var(--info) 15%, transparent)',
          filter: 'blur(80px)',
        }}
      />

      <div className="relative max-w-3xl">
        <div className="mb-8">
          <span className="da-type-eyebrow text-primary">Datacrate · Siting Intelligence</span>
          <h2 className="text-3xl sm:text-4xl font-black text-text-primary mt-3 tracking-tight">Stand up your first site</h2>
          <p className="text-lg text-text-secondary mt-3 max-w-xl leading-relaxed">
            Every decision starts with a site. Create one, attach your evidence, and the platform
            automatically assembles investment-grade GO / NO-GO intelligence around it.
          </p>
        </div>

        <ol className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {WELCOME_STEPS.map((s) => (
            <li
              key={s.step}
              className="relative overflow-hidden rounded-lg border border-border-subtle bg-background/80 px-5 py-5 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md"
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm mb-3">
                {s.step}
              </span>
              <p className="font-bold text-text-primary text-base">{s.title}</p>
              <p className="text-sm text-text-secondary mt-1.5 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            className="da-btn da-btn--primary px-6 py-2.5 text-sm font-bold shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all"
            data-testid="dashboard-welcome-new-site"
            onClick={() => {
              if (typeof window !== 'undefined') window.dispatchEvent(new Event('datacrate:new-site'));
            }}
          >
            + New Site
          </button>
        </div>
      </div>
    </section>
  );
}
